import { createContext, useContext, useEffect, useCallback, useState } from 'react';

const ThemeContext = createContext(undefined);

const STORAGE_KEY = 'palcare-theme';
const LIGHT = 'light';
const DARK = 'dark';

/* Theming is V2-ONLY.
   ---------------------------------------------------------------------------
   `data-theme` lives on <html>, so it is global by nature. Two things keep it
   off the classic UI:

     1. The toggle renders only in NavigationV2, which non-migrated users never
        see — their route table in App.jsx has no V2 page in it.
     2. `useForceLight()` pins light for the handful of V1 pages a MIGRATED
        user can still reach (Insights, AuditLogs/CampusLogs). Without it, a
        user who turned dark on a V2 page would carry it onto an unthemed
        classic page. See docs/DARK_MODE_PLAN.md section 3.

   Default is LIGHT. Light is what everyone uses today and must stay exactly as
   it is; dark is opt-in and remembered across sessions. */

function readStored() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === LIGHT || v === DARK) return v;
    } catch {
        // Private windows and blocked storage both land here.
    }
    return LIGHT;
}

/* Applied before React paints so a dark user does not get a flash of light on
   every load. index.html could do this earlier still with an inline script;
   see the plan's section 6 for why that is a follow-up rather than a
   requirement. */
function apply(theme) {
    try {
        document.documentElement.setAttribute('data-theme', theme);
    } catch {
        // Non-DOM environments (tests) simply skip this.
    }
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => {
        const initial = readStored();
        apply(initial);
        return initial;
    });

    useEffect(() => {
        apply(theme);
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // Preference simply will not persist; the app still works.
        }
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setThemeState((prev) => (prev === DARK ? LIGHT : DARK));
    }, []);

    const setTheme = useCallback((next) => {
        if (next === LIGHT || next === DARK) setThemeState(next);
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

/**
 * Pin light mode while an unthemed (V1 / classic) page is mounted, restoring
 * the user's real preference on unmount.
 *
 * Deliberately touches only the DOM attribute, never the stored preference —
 * visiting one classic page must not silently turn a user's dark mode off.
 *
 * `active` exists because a dispatcher can render V1 on one route and a V2
 * page on another: Tasks serves TasksEhrPage on /tasks/ehr but the unthemed
 * TasksMaint on /tasks/maintenance. Passing a flag keeps the hook call
 * unconditional, which the rules of hooks require, while the behaviour stays
 * per-route.
 */
export function useForceLight(active = true) {
    const { theme } = useTheme();
    useEffect(() => {
        if (!active) return undefined;
        apply(LIGHT);
        return () => apply(theme);
    }, [active, theme]);
}

export default ThemeContext;
